export type CollectionFrequency = 'weekly' | 'biweekly' | 'monthly';

export type CollectionTone = 'past_due' | 'friendly' | 'second_notice' | 'urgent';

// Tone order everywhere in the UI (mirrors Collections::EmailTemplate.tones).
export const TONES: CollectionTone[] = ['past_due', 'friendly', 'second_notice', 'urgent'];

export interface CollectionEmailTemplate {
  id?: number;
  tone: CollectionTone;
  enabled: boolean;
  frequency: CollectionFrequency;
  schedule_day: string | null;
  schedule_time: string;
  schedule_anchor_date: string | null; // ISO yyyy-mm-dd
  schedule_day_of_month: number | null; // 1–31
  subject: string;
  body: string;
}

export interface CollectionConfig {
  location: string;
  sender_display_name: string;
  reply_to_email: string;
  past_due_threshold_days: number;
  // 0 = off. Past Due clients at or under this many days are not emailed automatically.
  past_due_grace_days: number;
  friendly_threshold_days: number;
  second_notice_threshold_days: number;
  email_templates: CollectionEmailTemplate[];
  include_residential: boolean;
  residential_client_count: number;
  bucket_client_counts: Record<CollectionTone, number>;
  sending_enabled: boolean;
}

export const SCHEDULE_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;

export const FREQUENCIES = ['weekly', 'biweekly', 'monthly'] as const;

export const FREQUENCY_LABELS: Record<CollectionFrequency, string> = {
  weekly: 'Weekly',
  biweekly: 'Every Two Weeks',
  monthly: 'Monthly',
};

export const TONE_LABELS: Record<CollectionTone, string> = {
  past_due: 'Past Due',
  friendly: 'Friendly Reminder',
  second_notice: 'Second Notice',
  urgent: 'Urgent',
};

export const TEMPLATE_VARIABLES = [
  { variable: '{{company_name}}', description: 'Company name' },
  { variable: '{{total_past_due}}', description: 'Total past due amount' },
  { variable: '{{worst_aging_days}}', description: 'Days past due of the oldest past-due invoice' },
  { variable: '{{invoice_count}}', description: 'Number of past-due invoices' },
  {
    variable: '{{invoice_table}}',
    description: 'Past-due invoice table (rendered as a styled table in the email)',
  },
  {
    variable: '{{account_no}}',
    description: 'Customer account number',
  },
  {
    variable: '{{past_due_threshold_days}}',
    description: 'Days past due up to which a customer is in the Past Due tone',
  },
  {
    variable: '{{friendly_threshold_days}}',
    description: 'Days past due up to which a customer is in the Friendly tone',
  },
  {
    variable: '{{second_notice_threshold_days}}',
    description: 'Days past due above which a customer escalates to Urgent',
  },
];

export const MONTH_DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
